import React from 'react'

import dynamic from 'next/dynamic'
import fromPairs from 'lodash/fromPairs'
import isArray from 'lodash/isArray'
import mapValues from 'lodash/mapValues'
import {GetServerSideProps} from 'next'
import {ParsedUrlQuery} from 'querystring'
import urljoin from 'url-join';

import {
  fetchProductVariants,
  compileFilterString,
  AlgoliaSearchResults,
} from '@festi/common/api/algolia'
import {webpageQuery} from '@festi/common/api/datocms/queries'
import {getDatoWebpage, handleRestCache} from '@festi/common/api/global-cache'
import {restApi, VariantList} from '@festi/common/api/rest'
import {getAncestry} from '@festi/common/utils/categories'
import settings from '@festi/common/constants/settings'
import {getMetaDescription, getMetaTitle} from '@festi/common/utils/seo'
import {onDutyFree} from '@festi/common/constants/channels';

import {WebPage} from 'src/components/layout'
import {WebPageCommonProps} from 'src/components/layout/WebPage'
import {makePageCommonProps} from 'src/utils'

const SearchResults = dynamic(
  () => import('src/components/search/SearchResults'),
)

const CategoryResults = dynamic(
  () => import('src/components/search/redesign/CategoryResults'),
)

interface Props {
  id: number
  slug: string
  query: ParsedUrlQuery
  common: WebPageCommonProps
  attrNameMap: Record<string, string>
  initialData?: AlgoliaSearchResults<VariantList>
  origin: string
}

interface PageParams {
  slug: string
}

export const getServerSideProps: GetServerSideProps = async ({
  res,
  params,
  query,
  preview = false,
  req,
}) => {
  const {slug} = params as unknown as PageParams
  const q = mapValues(query, (v) => (isArray(v) ? v[0] : v))
  const id = parseInt(slug.split('-').pop() || '', 10)
  const page = Math.max(1, parseInt(q.page || '1', 10))

  const common = await makePageCommonProps(preview)

  const fieldTypes = await handleRestCache(`fieldtype_list`, () =>
    restApi.fieldTypesList(),
  )

  const attrNameMap = fromPairs(fieldTypes?.map((t) => [t.key, t.label]))

  const filters = compileFilterString(q)
  const initialData =
    !settings.redesign &&
    (await fetchProductVariants({
      categoryId: id,
      sortBy: q.sort || '',
      filters,
      query: '',
      page,
    }))

  if (common.categories.length === 0) {
    res.setHeader('Cache-Control', 's-maxage=1, stale-while-revalidate')
  } else {
    res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')
  }

  return {
    props: {
      id,
      slug,
      query,
      common,
      attrNameMap,
      initialData,
      preview,
      webPageSubscription: await getDatoWebpage(preview, webpageQuery),
      origin: req.headers.host,
    },
  }
}

export default function CategoryPage({
  id,
  query,
  initialData,
  attrNameMap,
  common,
}: Props) {
  const category = common?.categories?.find((c) => c.id === id);

  const ancestry = getAncestry(common?.categories, category);
  const breadCrumbs = ancestry.map((anc) => ({
    label: anc.name,
    path: `/voruflokkar/${anc.slug}/`,
  }));

  const name = category?.name || '';
  const metaTitle = getMetaTitle({category});
  const description = getMetaDescription({category});
  const baseUrl = onDutyFree ? settings.dutyfreeUrl : settings.baseUrl;
  const canonicalUrl = urljoin(baseUrl, `/voruflokkar/${category?.slug}`);

  return (
    <WebPage
      common={common}
      breadCrumbs={breadCrumbs}
      attrNameMap={attrNameMap}
      nextSeo={{
        seoTitle: metaTitle,
        seoDescription: description,
        seoCanonical: canonicalUrl,
      }}
    >
      {settings.redesign ? (
        <CategoryResults
          id={id}
          title={name}
          attrNameMap={attrNameMap}
          initialData={initialData}
          initialQuery={query}
        />
      ) : (
        <SearchResults
          id={id}
          common={common}
          title={name}
          description={description}
          attrNameMap={attrNameMap}
          initialData={initialData}
          isCategoryPage
        />
      )}
    </WebPage>
  );
}
